/**
 * Elite Fade Designs - Auth Service
 *
 * Wraps amazon-cognito-identity-js to provide a clean async API
 * for sign in / sign out / get session / get tokens / get groups.
 *
 * Supports the NEW_PASSWORD_REQUIRED challenge for first-time logins
 * (staff/admin users that received a temporary password via email).
 *
 * Token storage: sessionStorage (closing the tab signs the user out).
 * Refresh: handled automatically by the Cognito SDK while the tab is open.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

import { config } from '../config/index.js';

// ---------------------------------------------------------------------------
// Pool initialization
// ---------------------------------------------------------------------------
const userPool = new CognitoUserPool({
  UserPoolId: config.cognito.userPoolId,
  ClientId: config.cognito.clientId,
  // sessionStorage keeps users signed-in only for the current browser tab.
  Storage: window.sessionStorage,
});

// Module-level state for an in-progress NEW_PASSWORD_REQUIRED challenge.
// Only one challenge can be in flight per tab at a time. Cleared on
// success / failure / cancel.
let pendingChallenge = null;  // { user: CognitoUser, email: string }

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Sign in with email + password.
 *
 * @returns {Promise<SignInResult>}
 *   { status: 'SUCCESS', session }            on normal login
 *   { status: 'NEW_PASSWORD_REQUIRED', email } on first-time login
 *
 * Throws on auth failure (wrong password, user not found, etc.).
 */
export function signIn(email, password) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
      Storage: window.sessionStorage,
    });

    const auth = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    user.authenticateUser(auth, {
      onSuccess: (session) => {
        pendingChallenge = null;
        resolve({ status: 'SUCCESS', session: buildSessionInfo(session) });
      },
      onFailure: (err) => {
        pendingChallenge = null;
        reject(translateCognitoError(err));
      },
      newPasswordRequired: () => {
        // User must set a permanent password before sign-in completes.
        // We stash the CognitoUser instance so completeNewPassword() can
        // continue the flow from the UI.
        pendingChallenge = { user, email };
        resolve({ status: 'NEW_PASSWORD_REQUIRED', email });
      },
    });
  });
}

/**
 * Complete the NEW_PASSWORD_REQUIRED challenge with a permanent password.
 * Must be called within the same tab as the signIn() call that returned
 * NEW_PASSWORD_REQUIRED.
 *
 * @returns {Promise<SignInResult>} { status: 'SUCCESS', session }
 */
export function completeNewPassword(newPassword) {
  return new Promise((resolve, reject) => {
    if (!pendingChallenge) {
      const e = new Error('Session expired. Please sign in again.');
      e.code = 'NoPendingChallenge';
      reject(e);
      return;
    }

    const { user } = pendingChallenge;

    // Pass empty attributes — all user attributes were already set when
    // the admin created the user via AdminCreateUser. The required-attrs
    // list from Cognito should be empty for our flow.
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => {
        pendingChallenge = null;
        resolve({ status: 'SUCCESS', session: buildSessionInfo(session) });
      },
      onFailure: (err) => {
        // Keep challenge state alive on password validation errors so the
        // user can retry with a different password. Clear on hard failures.
        if (err?.code !== 'InvalidPasswordException') {
          pendingChallenge = null;
        }
        reject(translateCognitoError(err));
      },
    });
  });
}

/**
 * Cancel any in-progress NEW_PASSWORD_REQUIRED challenge.
 * Call when the user clicks "back" / "use a different email".
 */
export function cancelChallenge() {
  pendingChallenge = null;
}

/**
 * True if there is a NEW_PASSWORD_REQUIRED challenge waiting.
 */
export function hasPendingChallenge() {
  return pendingChallenge !== null;
}

/**
 * Sign the current user out and clear session storage.
 */
export function signOut() {
  pendingChallenge = null;
  const user = userPool.getCurrentUser();
  if (user) user.signOut();
}

/**
 * Returns the current session info if a valid (non-expired) session exists.
 * Returns null if no session, or if it cannot be refreshed.
 */
export function getCurrentSession() {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }

    user.getSession((err, session) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(buildSessionInfo(session));
    });
  });
}

export async function isAuthenticated() {
  const session = await getCurrentSession();
  return !!session;
}

export async function getIdToken() {
  const session = await getCurrentSession();
  return session ? session.idToken : null;
}

export async function hasAnyGroup(allowedGroups) {
  const session = await getCurrentSession();
  if (!session) return false;
  return allowedGroups.some((g) => session.groups.includes(g));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return {};
  }
}

function buildSessionInfo(session) {
  const idToken = session.getIdToken().getJwtToken();
  const accessToken = session.getAccessToken().getJwtToken();
  const refreshToken = session.getRefreshToken().getToken();
  const claims = decodeJwt(idToken);
  const rawGroups = claims['cognito:groups'];
  const groups = Array.isArray(rawGroups)
    ? rawGroups
    : (typeof rawGroups === 'string' ? rawGroups.split(',').filter(Boolean) : []);

  return {
    idToken,
    accessToken,
    refreshToken,
    email: claims.email,
    name: claims.name || claims.email,
    sub: claims.sub,
    groups,
    isAdmin: groups.includes('Admin'),
    isStaff: groups.includes('Staff'),
    isDispatcher: groups.includes('Dispatcher'),
    isCustomer: groups.includes('Customer'),
    expiresAt: claims.exp ? new Date(claims.exp * 1000) : null,
  };
}

/**
 * Translate raw Cognito errors into human-friendly Errors with stable codes.
 */
function translateCognitoError(err) {
  const code = err?.code || err?.name || 'UnknownError';
  const messages = {
    NotAuthorizedException: 'Incorrect email or password.',
    UserNotFoundException: 'No account found with that email.',
    UserNotConfirmedException: 'Account not yet confirmed. Contact support.',
    PasswordResetRequiredException: 'Password reset required. Contact support.',
    TooManyRequestsException: 'Too many attempts. Please wait a moment and try again.',
    TooManyFailedAttemptsException: 'Too many failed attempts. Please wait and try again.',
    InvalidParameterException: 'Invalid email or password format.',
    InvalidPasswordException: 'Password does not meet the requirements.',
    NetworkError: 'Network error. Check your connection and try again.',
    NoPendingChallenge: 'Session expired. Please sign in again.',
  };

  const e = new Error(messages[code] || err?.message || 'Sign in failed. Please try again.');
  e.code = code;
  e.original = err;
  return e;
}
